/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router";
import { useScopeParams } from "../hooks/useScopeParams";
import { getTrialIdFromPathname } from "../utils/scopeRouting";
import { useAskVivoEngine } from "../hooks/useAskVivoEngine";
import type { RouteConfig } from "../config/routes";
import type { ChatMessage } from "../components/Chat/chat";
import type {
  AskVivoRouteContext,
  AskVivoSendMessageOptions,
  AskVivoPanelState,
} from "../components/AskVivo/askVivo.types";
import { resolveAskVivoRouteContext } from "../components/AskVivo/askVivoRouteContext";
import { useAskVivoPanel } from "../hooks/useAskVivoPanel";

interface AskVivoContextValue {
  chatHistory: ChatMessage[];
  sessionId: string;
  isChatLoading: boolean;
  sendMessage: (prompt: string, options?: AskVivoSendMessageOptions) => void;
  resetSession: () => string;
  loadSession: (messages: ChatMessage[], sessionId: string) => void;
  routeContext: AskVivoRouteContext;
  panel: AskVivoPanelState;
  openPanel: (next: Exclude<AskVivoPanelState, { type: "none" }>) => void;
  closePanel: () => void;
  alertOnlyInsightIds: Set<string>;
  addAlertOnlyInsightId: (id: string) => void;
  completedChatActions: Set<string>;
  markChatActionCompleted: (interactionId: string) => void;
  hasChatActionCompleted: (interactionId: string) => boolean;
}

export const AskVivoContext = createContext<AskVivoContextValue | null>(null);

// Per-navigation id sets. Tagging the state with the nav key it was collected
// under lets a navigation drop the previous scope's ids during render, instead
// of clearing them from an effect one render later.
interface NavScopedIds {
  navKey: string;
  ids: Set<string>;
}

const EMPTY_IDS: Set<string> = new Set();

function addNavScopedId(
  navKey: string,
  id: string,
): (prev: NavScopedIds) => NavScopedIds {
  return (prev) =>
    prev.navKey === navKey
      ? { navKey, ids: new Set(prev.ids).add(id) }
      : { navKey, ids: new Set([id]) };
}

export function useAskVivo(): AskVivoContextValue {
  const ctx = use(AskVivoContext);
  if (!ctx) {
    throw new Error("useAskVivo must be used within AskVivoProvider");
  }
  return ctx;
}

function AskVivoStateProvider({
  children,
  askVivoRouteConfigs,
}: {
  children: ReactNode;
  askVivoRouteConfigs: RouteConfig[];
}) {
  const { scope, id } = useScopeParams();
  const location = useLocation();
  const { pathname } = location;
  const currentTrialId = getTrialIdFromPathname(pathname);
  const navKey = `${scope}/${id}`;
  const lastNavKeyRef = useRef<string | null>(null);
  const navResetEpochRef = useRef(0);
  const freshSessionSeenEpochRef = useRef(0);

  const routeContext = useMemo(
    () => resolveAskVivoRouteContext(pathname, scope, id, askVivoRouteConfigs),
    [pathname, scope, id, askVivoRouteConfigs],
  );

  const {
    chatHistory,
    sessionId,
    isChatLoading,
    sendMessage: sendAskVivoMessage,
    resetSession,
    loadSession,
  } = useAskVivoEngine();

  const { panel, openPanel, closePanel } = useAskVivoPanel();

  const [alertOnlyInsights, setAlertOnlyInsights] = useState<NavScopedIds>(
    () => ({ navKey, ids: new Set() }),
  );
  const alertOnlyInsightIds =
    alertOnlyInsights.navKey === navKey ? alertOnlyInsights.ids : EMPTY_IDS;
  const addAlertOnlyInsightId = useCallback(
    (insightId: string) => {
      setAlertOnlyInsights(addNavScopedId(navKey, insightId));
    },
    [navKey],
  );

  const [chatActions, setChatActions] = useState<NavScopedIds>(() => ({
    navKey,
    ids: new Set(),
  }));
  const completedChatActions =
    chatActions.navKey === navKey ? chatActions.ids : EMPTY_IDS;
  const markChatActionCompleted = useCallback(
    (interactionId: string) => {
      setChatActions(addNavScopedId(navKey, interactionId));
    },
    [navKey],
  );
  const hasChatActionCompleted = useCallback(
    (interactionId: string) => completedChatActions.has(interactionId),
    [completedChatActions],
  );

  const sendMessage = useCallback(
    (prompt: string, options?: AskVivoSendMessageOptions) => {
      sendAskVivoMessage(prompt, {
        ...options,
        // `trialId` says which trial the question was asked from: it is what the
        // history endpoint filters a trial's sessions by, and what the server
        // pins the agents to so the user does not have to name the trial in
        // every question. Trial detail screens live under the portfolio scope
        // (`/portfolio/trials/<id>/...`), so it is read from the path rather
        // than assumed to be the scope id. Genuine portfolio screens have no
        // single trial and send "".
        trialId: currentTrialId,
        // Route scope selects the agent: a single trial is answered by that
        // trial's own agent (SQL over the trial schema, plus protocol docs), the
        // portfolio by the client's cross-trial CDM agent. A trial with no
        // per-trial AskVivoConfigurations row at the installed biomind version
        // falls back server-side to the CDM agent pinned to that trial, so this
        // is safe to send before every trial's config has been generated.
        scope: currentTrialId ? "trial" : "portfolio",
      });
    },
    [currentTrialId, sendAskVivoMessage],
  );

  useEffect(() => {
    if (lastNavKeyRef.current === navKey) {
      return;
    }

    lastNavKeyRef.current = navKey;
    navResetEpochRef.current += 1;

    resetSession();
    closePanel();
  }, [navKey, resetSession, closePanel]);

  useEffect(() => {
    if (freshSessionSeenEpochRef.current !== navResetEpochRef.current) {
      // Effect 1 already reset for this navigation; skip to avoid double reset.
      freshSessionSeenEpochRef.current = navResetEpochRef.current;
      return;
    }
    if (routeContext.freshSession && !location.state?.question) {
      resetSession();
    }
  }, [
    location.state?.question,
    routeContext.freshSession,
    routeContext.routeSegment,
    resetSession,
  ]);

  const contextValue = useMemo(
    () => ({
      chatHistory,
      sessionId,
      isChatLoading,
      sendMessage,
      resetSession,
      loadSession,
      routeContext,
      panel,
      openPanel,
      closePanel,
      alertOnlyInsightIds,
      addAlertOnlyInsightId,
      completedChatActions,
      markChatActionCompleted,
      hasChatActionCompleted,
    }),
    [
      chatHistory,
      sessionId,
      isChatLoading,
      sendMessage,
      resetSession,
      loadSession,
      routeContext,
      panel,
      openPanel,
      closePanel,
      alertOnlyInsightIds,
      addAlertOnlyInsightId,
      completedChatActions,
      markChatActionCompleted,
      hasChatActionCompleted,
    ],
  );

  return <AskVivoContext value={contextValue}>{children}</AskVivoContext>;
}

export function AskVivoProvider({
  children,
  askVivoRouteConfigs,
}: {
  children: ReactNode;
  askVivoRouteConfigs: RouteConfig[];
}) {
  return (
    <AskVivoStateProvider askVivoRouteConfigs={askVivoRouteConfigs}>
      {children}
    </AskVivoStateProvider>
  );
}
